import {
  seqirusConfig,
  gskConfig,
  hexalConfig,
  incyteConfig,
  novartisConfig,
} from '@common/config';
import { Client, HexalInstance, NovartisInstance } from '@common/enums';
import { IInstanceConfig } from '@common/interfaces';
import {
  IMessage,
  INlpResponse,
  IQuestionOption,
  ITopic,
  ITopicGroup,
  MessageType,
  TNullable,
} from '@common/types';
import { CLIENT_ENV } from '@constants/env';
import { SUPPORTED_LANGUAGES, TSupportedLanguage } from '@constants/languages';
import { clsx, type ClassValue } from 'clsx';
import { TFunction } from 'i18next';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getDefaultLanguage(): TSupportedLanguage {
  return SUPPORTED_LANGUAGES.find((lng) => lng.isDefault)!;
}

function getInstanceFromUrl() {
  const pathParts = window.location.pathname.split('/');
  const instanceIndex = pathParts.indexOf('instance');
  return instanceIndex !== -1 ? pathParts[instanceIndex + 1] : null;
}
function getCurrentInstance(): IInstanceConfig {
  if (CLIENT_ENV) {
    const instance = getInstanceFromUrl();
    switch (CLIENT_ENV) {
      case Client.Incyte:
        if (instance) {
          const result = incyteConfig.instances.find((item) => item.name === instance);
          if (result) return result;
          throw new Error('[lib.utils]: No instance found');
        }
        return incyteConfig.instances[0];
      case Client.Hexal:
        if (instance) {
          const result = hexalConfig.instances.find((item) => item.name === instance);
          if (result) return result;
          throw new Error('[lib.utils]: No instance found');
        }
        return hexalConfig.instances[0];
      case Client.Novartis:
        if (instance) {
          const result = novartisConfig.instances.find((item) => item.name === instance);
          if (result) return result;
          throw new Error('[lib.utils]: No instance found');
        }
        return novartisConfig.instances[0];
      case Client.GSK:
        if (instance) {
          const result = gskConfig.instances.find((item) => item.name === instance);
          if (result) return result;
          throw new Error('[lib.utils]: No instance found');
        }
        return gskConfig.instances[0];
      case Client.Seqirus:
        if (instance) {
          const result = seqirusConfig.instances.find((item) => item.name === instance);
          if (result) return result;
          throw new Error('[lib.utils]: No instance found');
        }
        return seqirusConfig.instances[0];
      default:
        throw new Error('[lib.utils]: No instance found');
    }
  } else {
    throw new Error('[lib.utils]: No client detected');
  }
}
export const CURRENT_INSTANCE = getCurrentInstance();

export function getLangNamespace() {
  return CURRENT_INSTANCE.i18nNamespace;
}
export function getThemeName() {
  return CURRENT_INSTANCE.themeName;
}

export function getExactTopicFromTopicsList(
  topicsList: ITopic[],
  search: string
): TNullable<ITopic> {
  let foundTopic: TNullable<ITopic> = null;
  topicsList.forEach((topic) => {
    if (topic.keywords?.some((keyword) => normalizeQueryForTopicMatching(keyword) === search)) {
      foundTopic = topic;
      return;
    }
  });
  return foundTopic;
}
export function getExactGroupFromTopicGroupsList(
  topicGroups: ITopicGroup[],
  search: string
): TNullable<ITopicGroup> {
  const foundTopicGroupByName = topicGroups.find(
    (topicGroup) => normalizeQueryForTopicMatching(topicGroup.name) === search
  );
  if (foundTopicGroupByName) return foundTopicGroupByName;

  let foundTopicGroup: TNullable<ITopicGroup> = null;
  topicGroups.forEach((topicGroup) => {
    if (
      topicGroup.synonyms?.some((synonym) => normalizeQueryForTopicMatching(synonym) === search)
    ) {
      foundTopicGroup = topicGroup;
      return;
    }
  });
  return foundTopicGroup;
}
export function getOnlyDisabledTopicFromGroup(
  topics: ITopic[],
  topicGroup: TNullable<ITopicGroup>
): TNullable<ITopic> {
  if (!topicGroup) {
    return null;
  }

  const topicsInGroup = topics.filter((topic) => topic.groupId === topicGroup.id);
  if (topicsInGroup.length === 0) {
    return null;
  }

  return topicsInGroup.every((topic) => topic.status === 'disabled') ? topicsInGroup[0] : null;
}
export function findFirstTopicInQuery(query: string, topics: ITopic[]) {
  let matchedTopic: ITopic | undefined;
  let matchedKeywordLength = 0;

  for (const topic of topics) {
    if (topic.keywords?.length > 0) {
      for (const keyword of topic.keywords) {
        const normalizedKeyword = normalizeQueryForTopicMatching(keyword);
        if (normalizedKeyword && query.includes(normalizedKeyword)) {
          if (normalizedKeyword.length > matchedKeywordLength) {
            matchedTopic = topic;
            matchedKeywordLength = normalizedKeyword.length;
          }
        }
      }
    }
  }

  return matchedTopic;
}
export function findFirstTopicGroupInQuery(query: string, topicGroups: ITopicGroup[]) {
  for (const topicGroup of topicGroups) {
    if (query.includes(normalizeQueryForTopicMatching(topicGroup.name))) {
      return topicGroup;
    }
  }
  for (const topicGroup of topicGroups) {
    if (topicGroup.synonyms?.length > 0) {
      for (const synonym of topicGroup.synonyms) {
        if (query.includes(normalizeQueryForTopicMatching(synonym))) {
          return topicGroup;
        }
      }
    }
  }
}

export function trimQuery(query: string) {
  return query.trim();
}

export function normalizeQueryForTopicMatching(query: string) {
  return trimQuery(query)
    .toLocaleLowerCase()
    .replace(/[\p{P}\p{S}]+/gu, '');
}

export function convertQueryToMessage(query: string, total: number): IMessage {
  const id = ++total;
  return {
    id,
    message: query,
    type: MessageType.UserEntry,
  };
}
export function convertResponseToMessage(
  response: INlpResponse,
  total: number,
  t: TFunction
): IMessage {
  if (!response?.questionOptions) {
    const message = t('common.general_error');
    return {
      id: total,
      message: message || 'error',
      type: MessageType.Error,
    };
  }

  const firstOption = response.questionOptions[0];

  if (!firstOption) {
    return {
      id: total,
      message: t('chat.response.no_answer'),
      type: MessageType.NoAnswerResponse,
    };
  }

  const option = firstOption.optionType;
  if (option === 'CERTAIN_OPTION') {
    return questionOptionToMessage(
      firstOption,
      response.questionOptions,
      response.query!.id,
      t,
      total
    );
  }
  return {
    id: total,
    message: t('chat.response.not_sure'),
    choices: response.questionOptions,
    type: MessageType.ChoiceResponse,
  };
}
export function questionOptionToMessage(
  question: IQuestionOption,
  questionOptions: IQuestionOption[],
  queryId: string,
  t: TFunction,
  total: number
): IMessage {
  const type = question.status === 'infoservice' ? MessageType.ForceMedInfo : MessageType.Response;
  return {
    id: total,
    questionQuery: question.actualQuery,
    questionId: question.questionId,
    message: question.answer || t('chat.response.no_text'),
    queryId,
    type,
    references: question.references || [],
    choices: questionOptions,
  };
}
export function shouldUseSessionStorage() {
  const instancesUsingSessionStorage: string[] = [HexalInstance.Default];
  return instancesUsingSessionStorage.includes(CURRENT_INSTANCE.name);
}
export function shouldUseMaxHight() {
  const instancesUsingMaxHight: string[] = [HexalInstance.Default];
  return instancesUsingMaxHight.includes(CURRENT_INSTANCE.name);
}
export function shouldDisableSearchFieldAutoFocus() {
  const instancesWithDisabledInput: string[] = [HexalInstance.Default];
  return instancesWithDisabledInput.includes(CURRENT_INSTANCE.name);
}

export function isNovartisInstance() {
  return CURRENT_INSTANCE.name === NovartisInstance.Default;
}

export function buildNovartisContactFormUrl(query: string) {
  const trimmedQuery = trimQuery(query);
  const searchParams = new URLSearchParams({
    message: trimmedQuery,
  });

  return `https://klinischeforschung.novartis.de/infoservice/?${searchParams.toString()}#modal-kontaktformular`;
}

export const QUERY_PARAM = 'query';
export const SUBMIT_QUERY_PARAM = 'submitQuery';
